import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';

/**
 * Axios instance configuration
 *
 * Provides a pre-configured HTTP client with:
 * - Base URL from environment variables
 * - Authentication token handling
 * - Request/response interceptors
 * - Error handling and retry logic
 */

// Get base URL from environment variables or use default
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

// Create axios instance with default config
export const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 180000, // 180 seconds (3 minutes) - permit extraction can take time
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies in requests
});

/**
 * Request interceptor
 * Adds authentication token to all requests
 */
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get token from localStorage
    const token = localStorage.getItem('access_token');

    // Add authorization header if token exists
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log request in development
    if (import.meta.env.DEV) {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data,
      });
    }

    return config;
  },
  (error: AxiosError) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

/**
 * Response interceptor
 * Handles successful responses and errors globally
 */
axiosInstance.interceptors.response.use(
  (response) => {
    // Log response in development
    if (import.meta.env.DEV) {
      console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data,
      });
    }

    return response;
  },
  async (error: AxiosError<{ detail?: string; message?: string }>) => {
    const originalRequest = error.config;

    // Log error in development
    if (import.meta.env.DEV) {
      console.error('[API Response Error]', {
        url: error.config?.url,
        status: error.response?.status,
        message: error.response?.data?.detail || error.response?.data?.message || error.message,
      });
    }

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      // Clear stored authentication data
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');

      // Try to refresh token if we have a refresh token
      const refreshToken = localStorage.getItem('refresh_token');

      if (refreshToken && originalRequest && !('_retry' in originalRequest)) {
        // Mark request as retried to prevent infinite loops
        (originalRequest as InternalAxiosRequestConfig & { _retry?: boolean })._retry = true;

        try {
          // Attempt to refresh the access token
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token } = response.data;
          localStorage.setItem('access_token', access_token);

          // Retry the original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
          }
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          // Refresh failed, redirect to login
          console.error('[Token Refresh Failed]', refreshError);

          // Only redirect if we're not already on the login page
          if (window.location.pathname !== '/login') {
            window.location.href = '/login?expired=true';
          }

          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token or already retried, redirect to login
        if (window.location.pathname !== '/login') {
          window.location.href = '/login?expired=true';
        }
      }
    }

    // Handle 403 Forbidden errors
    if (error.response?.status === 403) {
      console.warn('[Forbidden] Insufficient permissions');
      // You can add a toast notification here
    }

    // Handle 404 Not Found errors
    if (error.response?.status === 404) {
      console.warn('[Not Found] Resource not found');
    }

    // Handle 500+ Server errors
    if (error.response?.status && error.response.status >= 500) {
      console.error('[Server Error] Internal server error');
      // You can add a toast notification here
    }

    // Handle network errors
    if (!error.response) {
      console.error('[Network Error] Unable to reach the server');
      // You can add a toast notification here
    }

    return Promise.reject(error);
  }
);

/**
 * Helper function to extract error message from API error
 */
export const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ detail?: string; message?: string }>;
    return (
      axiosError.response?.data?.detail ||
      axiosError.response?.data?.message ||
      axiosError.message ||
      'An unexpected error occurred'
    );
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred';
};

/**
 * Helper function to check if error is an authentication error
 */
export const isAuthError = (error: unknown): boolean => {
  if (axios.isAxiosError(error)) {
    return error.response?.status === 401;
  }
  return false;
};

/**
 * Helper function to check if error is a permission error
 */
export const isPermissionError = (error: unknown): boolean => {
  if (axios.isAxiosError(error)) {
    return error.response?.status === 403;
  }
  return false;
};
